import React, { useEffect, useState } from 'react';
import { Device, Call } from '@twilio/voice-sdk';
import {
  Button,
  Box,
  Typography,
  CircularProgress,
} from '@mui/material';
import {
  Phone as PhoneIcon,
  CallEnd as CallEndIcon,
  Mic as MicIcon,
  MicOff as MicOffIcon,
} from '@mui/icons-material';
import { api, callLog } from '../services/api';
import { format } from 'date-fns';


interface BrowserPhoneProps {
  contactNumber: string;
  onCallEnd?: () => void;
}

export const BrowserPhone: React.FC<BrowserPhoneProps> = ({ contactNumber, onCallEnd }) => {
  const [device, setDevice] = useState<Device | null>(null);
  const [call, setCall] = useState<Call | null>(null);
  const [isMuted, setIsMuted] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);

  useEffect(() => {
    setupDevice();
    const tokenRefreshInterval = setInterval(setupDevice, 45 * 60 * 1000);
  
    return () => {
      if (device) {
        device.destroy();
      }
    };
  }, []);

  const setupDevice = async () => {
    try {
      // Get token from backend
      const response = await api.get('api/calls/get_token/');
      const token = response.data.token;

      // Initialize Twilio device
      const newDevice = new Device(token);

      await newDevice.register();
      setDevice(newDevice);

      // Set up event listeners
      newDevice.on('error', (error) => {
        console.error('Twilio device error:', {
          code: error.code,
          message: error.message,
          explanation: error.explanation
        });
      });
    } catch (error) {
      console.error('Error setting up device:', error);
    }
  };

  const makeCall = async () => {
    // if (!device) return;

    if (!device || device.state !== 'registered') {
      console.error('Twilio Device not registered yet!');
      return;
    }

    try {
      setIsConnecting(true);
      const newCall = await device.connect({
        params: {
          To: contactNumber,
          
        }
      });
      // console.log('Call started:', newCall);
      newCall.on('accept', () => {
        console.log('Call accepted');
        setIsConnecting(false);
        setCall(newCall);
      });
      
      newCall.on('error', (error) => {
        console.error('Call Error:', {
          code: error.code,
          message: error.message,
          explanation: error.explanation,
          connectionError: error.twilioError,
          callStatus: newCall.status(),
        });
        setIsConnecting(false);
      });
      
      newCall.on('disconnect', async () => {
        console.log('Call disconnected');

        const endTime = new Date();
  
        // Prepare call log data
        const callLogData = {
          start_time: format(new Date(), "yyyy-MM-dd'T'HH:mm:ss'Z'"),
          end_time: format(endTime, "yyyy-MM-dd'T'HH:mm:ss'Z'"),
          duration: Math.floor((endTime.getTime() - new Date().getTime()) / 1000),
          status: 'completed',
          call_sid: newCall.parameters.CallSid,
          contact: contactNumber, // Assuming you have access to contact ID
        };
        try {
          // Call the callLog API function
          await callLog(callLogData);
        } catch (error) {
          console.error('Error logging call:', error);
        }

        



        setCall(null);
        setIsConnecting(false);
        if (onCallEnd) onCallEnd();
      });
      // setCall(newCall);
    } catch (error) {
      console.error('Error making call:', error);
      setIsConnecting(false);
    }
  };

  const endCall = () => {
    if (call) {
      call.disconnect();
      setCall(null);
    }
  };

  const toggleMute = () => {
    if (call) {
      if (isMuted) {
        call.mute(false);
      } else {
        call.mute(true);
      }
      setIsMuted(!isMuted);
    }
  };

  return (
    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
      {!call ? (
        <Button
          variant="contained"
          color="primary"
          startIcon={isConnecting ? <CircularProgress size={20} /> : <PhoneIcon />}
          onClick={makeCall}
          disabled={!device || isConnecting}
        >
          {isConnecting ? 'Connecting...' : 'Call'}
        </Button>
      ) : (
        <>
          <Typography variant="body2" color="success.main">
            Call in progress
          </Typography>
          <Button
            variant="contained"
            color="secondary"
            onClick={toggleMute}
            startIcon={isMuted ? <MicOffIcon /> : <MicIcon />}
          >
            {isMuted ? 'Unmute' : 'Mute'}
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={endCall}
            startIcon={<CallEndIcon />}
          >
            End Call
          </Button>
        </>
      )}
    </Box>
  );
};
